import secureStorage from '../../lib/secureStorage';
import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, Loader2, Database } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import logger from '../../lib/logger';
import api from '../../lib/api';
import LegacyHistoryPanel from './LegacyHistoryPanel';
import TransferEvidenceSeedPanel from './TransferEvidenceSeedPanel';

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const SEED_WINDOW = (() => { const now = new Date(); return { start: ym(new Date(now.getFullYear(), now.getMonth() - 11, 1)), end: ym(now) }; })();

function LEIPEKPISeedSection({ reseedTick = 0 }) {
  const [seeding, setSeeding] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);

  // Poll progress while seeding is active
  useEffect(() => {
    if (!seeding) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get('/admin/seed/seed-lei-pe-kpi/progress');
        const data = res.data;
        setProgress(data);
        if (data.status === 'complete') {
          setSeeding(false);
          setResult(data);
          toast.success(`Seeded ${data.total} records successfully!`);
        } else if (data.status === 'failed') {
          setSeeding(false);
          toast.error('Seed task failed. Check backend logs.');
        }
      } catch (_) { /* silent poll failure */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [seeding]);

  const handleSeed = async () => {
    setSeeding(true);
    setResult(null);
    setProgress(null);
    try {
      await api.post('/admin/seed/seed-lei-pe-kpi', {});
    } catch (err) {
      logger.error('LEI/PE/KPI seed failed:', err);
      toast.error(`Seed failed: ${err.response?.data?.detail || err.message}`);
      setSeeding(false);
    }
  };

  useEffect(() => { if (reseedTick > 0) handleSeed(); }, [reseedTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const PHASE_LABELS = {
    initializing: 'Initializing...',
    cleaning_old_data: 'Cleaning old seed data...',
    generating_records: 'Generating records...',
    inserting_lei: 'Inserting LEI data...',
    inserting_pe: 'Inserting PE data...',
    inserting_kpi: 'Inserting KPI data...',
    complete: 'Complete!',
  };

  const pct = progress?.target > 0 ? Math.min(100, Math.round((progress.total / progress.target) * 100)) : 0;

  return (
    <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div>
        <h4 className="text-sm font-bold text-blue-900">LEI, PE Capability & KPI Impact Data</h4>
        <p className="text-xs text-blue-700 mt-0.5">Synthetic data for 6 orgs, 23 cohorts, rolling last 12 months ({SEED_WINDOW.start} → {SEED_WINDOW.end}). Includes 15 LEI measures, PE Capability scores, and 10 KPI metrics on the same monthly grid.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={handleSeed} disabled={seeding} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="seed-lei-pe-kpi-btn">
          {seeding ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Database className="h-3.5 w-3.5 mr-1.5" />}
          {seeding ? 'Seeding...' : 'Seed LEI/PE/KPI Data'}
        </Button>
        <span className="text-xs text-blue-600">|</span>
        <a href="/downloads/seed_data/lei_measures_synthetic.csv" download className="text-xs text-blue-600 hover:underline">LEI CSV</a>
        <a href="/downloads/seed_data/pe_capability_synthetic.csv" download className="text-xs text-blue-600 hover:underline">PE CSV</a>
        <a href="/downloads/seed_data/kpi_impact_synthetic.csv" download className="text-xs text-blue-600 hover:underline">KPI CSV</a>
      </div>

      {/* Live progress indicator */}
      {seeding && progress && progress.status === 'in_progress' && (
        <div className="space-y-2 p-3 bg-blue-100 rounded-lg border border-blue-200" data-testid="seed-progress-panel">
          <div className="flex items-center justify-between text-xs text-blue-900">
            <span className="font-medium flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" />
              {PHASE_LABELS[progress.phase] || progress.phase}
            </span>
            <span className="font-bold">{pct}%</span>
          </div>
          <div className="w-full h-2 bg-blue-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${pct}%` }}
              data-testid="seed-progress-bar"
            />
          </div>
          <div className="flex gap-3 text-xs text-blue-700">
            <span>LEI: <strong>{progress.lei || 0}</strong></span>
            <span>PE: <strong>{progress.pe || 0}</strong></span>
            <span>KPI: <strong>{progress.kpi || 0}</strong></span>
            <span className="ml-auto">{progress.total || 0} / {progress.target || '?'}</span>
          </div>
        </div>
      )}

      {/* Completion result */}
      {!seeding && result && result.status === 'complete' && (
        <div className="text-xs text-emerald-800 bg-emerald-100 rounded p-2 flex items-center gap-1.5" data-testid="seed-complete-panel">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
          Seeded: LEI {result.lei} rows | PE {result.pe} rows | KPI {result.kpi} rows | Total: {result.total}
        </div>
      )}
    </div>
  );
}

export default function SeedDataTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [startDate, setStartDate] = useState(SEED_WINDOW.start);
  const [endDate, setEndDate] = useState(SEED_WINDOW.end);
  const [force, setForce] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [results, setResults] = useState(null);
  const [reseedTick, setReseedTick] = useState(0);

  const API = process.env.REACT_APP_BACKEND_URL;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const res = await fetch(`${API}/api/admin/seed/status`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setStatus(await res.json());
    } catch (err) { logger.error('Failed to load seed status:', err); }
    setLoading(false);
  }, [API]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleSeed = async () => {
    setSeeding(true);
    setResults(null);
    try {
      const token = secureStorage.getItem('session_token');
      const body = { start_date: startDate, end_date: endDate, force };
      if (selectedOrgs.length > 0) body.organizations = selectedOrgs;
      const res = await fetch(`${API}/api/admin/seed/seed-roe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data);
        loadStatus();
      }
    } catch (err) { logger.error('Seed operation failed:', err); toast.error('Seed operation failed'); }
    setSeeding(false);
  };

  const toggleOrg = (orgId) => {
    setSelectedOrgs(prev => prev.includes(orgId) ? prev.filter(id => id !== orgId) : [...prev, orgId]);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-500" /></div>;

  return (
    <Card data-testid="seed-data-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Database size={18} /> ROE Seed Data</CardTitle>
        <CardDescription>Load synthetic ROE data (L2 assessments, Motivation surveys, L3 observations) for testing and demos.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Date Range */}
        <div className="flex items-end gap-4">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Start Date</label>
            <Input type="month" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" data-testid="seed-start-date" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">End Date</label>
            <Input type="month" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" data-testid="seed-end-date" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="force-seed" checked={force} onChange={e => setForce(e.target.checked)} className="rounded" data-testid="seed-force" />
            <label htmlFor="force-seed" className="text-xs text-slate-600">Force replace existing data</label>
          </div>
        </div>

        {/* Org Selection */}
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">Select Organizations {selectedOrgs.length === 0 && <span className="text-xs text-slate-400 font-normal">(none selected = seed all empty orgs)</span>}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {(status?.organizations || []).map(org => (
              <button
                key={org.org_id}
                onClick={() => toggleOrg(org.org_id)}
                className={`p-3 rounded-lg border text-left transition-all ${
                  selectedOrgs.includes(org.org_id) ? 'border-teal-300 bg-teal-50' : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`seed-org-${org.org_id}`}
              >
                <p className="text-sm font-medium text-slate-800">{org.org_name}</p>
                <div className="flex gap-2 mt-1 text-xs text-slate-500">
                  <span>L2: {org.l2_count}</span>
                  <span>Mot: {org.motivation_count}</span>
                  <span>L3: {org.l3_count}</span>
                </div>
                <Badge variant="outline" className={`mt-1 text-xs ${org.has_data ? 'text-teal-600 border-teal-200' : 'text-slate-400'}`}>
                  {org.has_data ? 'Has Data' : 'Empty'}
                </Badge>
              </button>
            ))}
          </div>
        </div>

        {/* Seed Button - ROE */}
        <Button onClick={handleSeed} disabled={seeding} className="bg-teal-600 hover:bg-teal-700" data-testid="run-seed-btn">
          {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
          {seeding ? 'Seeding...' : `Seed ROE Data (${startDate} to ${endDate})`}
        </Button>

        {/* Seed Button - LEI/PE/KPI */}
        <LEIPEKPISeedSection reseedTick={reseedTick} />

        {/* Legacy LEI/KPI purge */}
        <LegacyHistoryPanel organizations={status?.organizations || []} onReseed={() => setReseedTick((t) => t + 1)} />
        <TransferEvidenceSeedPanel organizations={status?.organizations || []} />

        {/* Results */}
        {results && (
          <div className="space-y-2" data-testid="seed-results">
            <p className="text-sm font-medium text-slate-700">Seed Results ({results.date_range?.start} to {results.date_range?.end})</p>
            {results.results?.map(r => (
              <div key={r.org} className={`flex items-center justify-between p-2.5 rounded-lg border ${r.status === 'seeded' ? 'border-teal-200 bg-teal-50' : r.status === 'skipped' ? 'border-amber-200 bg-amber-50' : 'border-slate-200'}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{r.org}</p>
                  <p className="text-xs text-slate-500">
                    {r.status === 'skipped' ? r.reason : `L2: ${r.l2} | Mot: ${r.motivation} | L3: ${r.l3}`}
                    {r.source === 'synthetic' && r.status === 'seeded' && (
                      <span className="ml-1.5 text-indigo-600 font-medium">· generated (no CSV)</span>
                    )}
                  </p>
                </div>
                <Badge className={`text-xs ${r.status === 'seeded' ? 'bg-teal-100 text-teal-700' : r.status === 'skipped' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{r.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
